import { createApp } from 'vue';
import { Dialog, Loading, Notify, Quasar } from 'quasar';
import langDe from 'quasar/lang/de';

import '@quasar/extras/roboto-font/roboto-font.css';
import '@quasar/extras/material-icons/material-icons.css';
import '@quasar/extras/material-icons-outlined/material-icons-outlined.css';
import '@quasar/extras/mdi-v7/mdi-v7.css';
import 'quasar/src/css/index.sass';
import '@/css/app.scss';

import App from './App.vue';
import initAuth from './boot/initAuth';
import pinia from './stores';

const PREVIEW_GUARD_HOST = 'prod.totaltrivial.de';
const PREVIEW_GUARD_QUERY_PARAM = 'key';
const PREVIEW_GUARD_SESSION_KEY = 'tt-preview-access-ok';
const DARK_MODE_STORAGE_KEY = 'tt-dark-mode';

/** Returns true when the production preview guard should run on this host. */
const shouldRunPreviewGuard = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const expectedKey = String(import.meta.env.VITE_PREVIEW_ACCESS_KEY ?? '').trim();
  if (!expectedKey) {
    return false;
  }

  return window.location.hostname === PREVIEW_GUARD_HOST;
};

/** Verifies preview access using query param and session storage. */
const isPreviewAccessGranted = () => {
  const expectedKey = String(import.meta.env.VITE_PREVIEW_ACCESS_KEY ?? '').trim();
  if (!expectedKey) {
    return true;
  }

  const url = new URL(window.location.href);
  const queryKey = url.searchParams.get(PREVIEW_GUARD_QUERY_PARAM)?.trim() ?? '';
  const hasSessionAccess =
    sessionStorage.getItem(PREVIEW_GUARD_SESSION_KEY) === 'true';

  if (queryKey && queryKey === expectedKey) {
    sessionStorage.setItem(PREVIEW_GUARD_SESSION_KEY, 'true');
    url.searchParams.delete(PREVIEW_GUARD_QUERY_PARAM);
    window.history.replaceState({}, document.title, url.toString());
    return true;
  }

  return hasSessionAccess;
};

/** Renders a plain 404 page for blocked preview access. */
const renderNotFoundPage = () => {
  const appRoot = document.querySelector('#app');
  if (!appRoot) {
    return;
  }

  appRoot.innerHTML = `
    <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#fff;color:#111;font-family:Arial,sans-serif;">
      <section style="max-width:640px;text-align:center;">
        <h1 style="margin:0 0 12px;font-size:28px;line-height:1.2;">404</h1>
        <p style="margin:0;font-size:16px;line-height:1.5;">Not Found</p>
      </section>
    </main>
  `;
};

/** Resolves the initial Quasar dark-mode state before the app mounts. */
const resolveInitialDarkMode = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedValue = window.localStorage.getItem(DARK_MODE_STORAGE_KEY);
  if (storedValue == null) {
    return true;
  }

  return storedValue === 'true';
};

async function start() {
  if (shouldRunPreviewGuard() && !isPreviewAccessGranted()) {
    renderNotFoundPage();
    return;
  }

  const app = createApp(App);

  app.use(pinia);

  try {
    await initAuth(pinia);
  } catch (error) {
    console.error('[Boot] Failed to initialize authentication', error);
  }

  const { default: router } = await import('./router');

  app.use(router);
  app.use(Quasar, {
    config: {
      dark: resolveInitialDarkMode(),
    },
    lang: langDe,
    plugins: {
      Dialog,
      Loading,
      Notify,
    },
  });

  app.mount('#app');
}

start();
